import ClockRewindIcon from '$assets/svgs/clock-rewind.svg?react';
import SunriseIcon from '$assets/svgs/sunrise.svg?react';
import SunsetIcon from '$assets/svgs/sunset.svg?react';
import MoonStarIcon from '$assets/svgs/moon-star.svg?react';
import {BookingPreferredTime, Consultant} from './types';

export const BOOKING_PREFERRED_TIMES: {
  title: string;
  id: BookingPreferredTime;
  icon: React.ReactNode;
}[] = [
  {
    id: BookingPreferredTime.All,
    title: 'All times are fine',
    icon: <ClockRewindIcon />,
  },
  {
    id: BookingPreferredTime.Morning,
    title: '08:00 - 12:00',
    icon: <SunriseIcon />,
  },
  {
    id: BookingPreferredTime.Afternoon,
    title: '13:00 - 17:30',
    icon: <SunsetIcon />,
  },
  {
    id: BookingPreferredTime.Evening,
    title: '18:00 - 19:30',
    icon: <MoonStarIcon />,
  },
];

export const enum BookingScreenColors {
  WrapperBackground = 'linear-gradient(118.28deg, #FFF1D5 1.91%, #F3F3FF 77.03%)',
  SeeAllGradient = 'linear-gradient(180deg, #6100FF 0%, #FE7235 100%)',

  StepperBottomBarGradient = 'linear-gradient(90deg, #7625DC 0%, #B2B4FF 343.21%)',

  MenuItemTitle = '#344054',
  MenuItemBackground = '#D4D7DE',
}

export const TITLE_ITEMS = [
  'Mr',
  'Mrs',
  'Miss',
  'Ms',
  'Mx',
  'Ind',
  'M',
  'Misc',
  'Dr',
  'Prof',
  'Rev',
  'Hon',
];

export const GENDER_ITEMS = [
  'Male',
  'Female',
  'Cisgender (Cis)',
  'Transgender (Trans)',
  'Non-binary',
  'Genderqueer',
  'Genderfluid',
  'Agender',
  'Bigender',
];

export const CONTACT_US = 'tel:01162708033';

export const NO_PREFERENCE_CONSULTANT: Consultant = {
  id: NaN,
  fullName: "I don't have a preferred consultant",
  profileUrl: '',
  doctorDetails: null,
  priceLists: null,
};

export const PATIENT_CONTACT_DETAIL_CHECKBOX_LABEL =
  'I consent to my Personal Data being used for information to be sent to me which is relevant to me and/or my treatment. This may include updates on new clinical products, services or patients events regarding SEH. For further information on how your data is used, Read our Privacy Policy';

export const MAX_FILES = 1;
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const ACCEPTED_FILES = {
  'image/*': ['.jpg', '.jpeg', '.png', '.docx'],
  'application/pdf': ['.pdf'],
};

export const ERROR_MESSAGES = {
  PAYMENT_FORM_NOT_INITIALIZED: 'Payment form not initialized',
  PAYMENT_METHOD_FAILED:
    'Failed to create payment method. Please check your card details.',
  APPOINTMENT_CREATION_FAILED: 'Failed to create appointment before payment.',
  PAYMENT_PROCESSING_FAILED: 'Payment processing failed. Please try again.',
};

export const PAYMENT_PROVIDER = {
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
};

export const BUTTON_TEXT = {
  SELECT_PAYMENT_OPTION: 'Please select a payment option',
  PAY_AND_SUBMIT: 'Pay and submit booking request',
  SUBMIT_REQUEST: 'Submit booking request',
};

export const DEFAULT_VALUES = {
  PATIENT_ID: 0,
  CONSULTANT_ID: 0,
  CONSULTATION_FEE: 0,
  EMPTY_STRING: '',
  CIRCLE_PROGRESS_SIZE: 50,
  STACK_GAP: 3,
  FLEX_GAP: 5,
  INNER_STACK_GAP: 1,
};

export const INSURANCE_DETAIL_VIEW_TEXT = {
  PAYMENT_DETAILS: 'Payment details',
  PAYMENT_METHOD: 'Payment method',
  INSURANCE_DETAILS: 'Insurance details',
  FILL_DETAILS:
    'Please fill in the details below to continue with the booking process',
  SELECT_PAYMENT: 'Select a payment method to confirm your booking',
} as const;

export const REVIEW_CANCEL_TEXTS = {
  NOT_SPECIFIED: 'Not specified',
  CONSULTATION: 'consultation',
  ERROR_DEFAULT: 'An error occurred. Please try again.',
  ERROR_MISSING_ID: 'Appointment ID is missing',
  ERROR_MISSING_REASON: 'Cancellation reason is required',
  ERROR_UNAUTHORIZED:
    'You are not authorized to cancel this appointment. Please log in again or contact support.',
  ERROR_NOT_FOUND:
    'The appointment could not be found. It may have already been cancelled.',
  ERROR_BAD_REQUEST:
    'Invalid cancellation request. Please check your information and try again.',
  ERROR_GENERIC:
    'Failed to cancel appointment. Please try again or contact support.',
  ERROR_LOG_PREFIX: 'Failed to cancel appointment:',
  ERROR_OTHER_REASON_REQUIRED: 'Please provide a reason for cancellation',
  CANCEL_BACKGROUND_ALT: 'Cancel background',
  CANCEL_ICON_ALT: 'Cancel icon',
  TITLE: 'Your cancellation',
  DESCRIPTION:
    'Your cancellation is effective immediately. If a refund is applicable, we will process it according to our cancellation policy. A member of our team will contact you by phone to confirm your preferred refund method.',
  SUMMARY_TITLE: 'Your appointment cancellation summary:',
  LABEL_APPOINTMENT: 'Appointment',
  LABEL_CONSULTANT: 'Consultant',
  LABEL_DATE: 'Date',
  LABEL_TIME: 'Time',
  BUTTON_CANCEL: 'Cancel',
  BUTTON_CONFIRM: 'Confirm cancellation',
  OTHER: 'Other (please specify)',
} as const;

export const PAYMENT_ERROR_MESSAGES = {
  PAYMENT_FORM_NOT_READY:
    'The payment form isn’t ready yet. Please refresh the page and try again.',
  INVALID_CARD_DETAILS:
    'We couldn’t process your payment. Please check your card number, expiry date, and CVV, then try again.',
  PAYMENT_FAILED:
    'Your payment didn’t go through. Please try again or contact our support team for help.',
  PAYPAL_FAILED:
    'We couldn’t process your PayPal payment. Please try again or use another payment method.',
  PAYPAL_NO_URL:
    'We couldn’t start your PayPal payment. Please refresh and try again.',
  PAYMENT_PROCESSING_ERROR:
    'Your payment went through, but we couldn’t confirm it. Please contact our support team for assistance.',
  UNEXPECTED_ERROR:
    'Something went wrong. Please try again or contact support if the issue continues.',
  CARD_PAYMENT_ERROR:
    'We ran into an issue while processing your card payment. Please try again.',
  LOADING_ERROR:
    'We couldn’t load the payment details. Please check the link and try again.',
} as const;
